import { Sidebar } from 'primereact/sidebar';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import ConfirmationDialog from '../../../../components/messages/ConfirmationDialog';
import LoadingButton from '../../../../components/inputs/LoadingButton';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { postClaim } from '../../../../services/Insurance/Claim';
import {
  ClaimForm,
  ClaimStatus,
  NewClaimArgs,
} from '../../../../Interfaces/Insurance/Claim';
import { useClaimSummaryContext } from './../ClaimContext';
import { ClaimFiltersOptions } from './../ClaimFilters';
import ClaimsForm from '../forms/ClaimForm';
import { Status } from '@/src/app/Interfaces/system/Generic.interfaces';

type NewClaimDrilldownProps = {
  visible: boolean;
  setVisible: (value: boolean) => void;
};

const NewClaimDrilldown = ({ visible, setVisible }: NewClaimDrilldownProps) => {
  const { selectedCompany } = useCompanyContext();
  const { addOneAtStart } = useClaimSummaryContext();
  const { getValues } = useFormContext<ClaimFiltersOptions>();
  const formMethods = useForm<ClaimForm>({ defaultValues: { contacts: [{}] } });
  const { handleSubmit, reset } = formMethods;
  const toast = useRef<Toast>(null);

  const createClaim = useMutation({
    mutationFn: (data: NewClaimArgs) => {
      return postClaim(selectedCompany!, data);
    },
    onSuccess: (claim) => {
      reset();
      const status = getValues('claimStatus');
      if (status === 'all' || status === ClaimStatus.Active) {
        addOneAtStart(claim);
      }
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Claim Creation Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: ClaimForm) => {
    const { logs, contacts, status, relation, ...otherData } = data;
    const cleanedContacts = contacts?.filter((contact) => contact.name);

    createClaim.mutate({
      ...otherData,
      contacts:
        (cleanedContacts?.length ?? 0) > 0 ? cleanedContacts : undefined,
    });
  };

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={() => {
        setVisible(false);
        reset();
        createClaim.reset();
      }}
      className="w-full max-w-600px"
    >
      <div className="blackText text-standard printBackground max-w-800px">
        <div className="flex flex-column row-gap-4 sm:row-gap-2 sm:flex-row flex-wrap align-items-center justify-content-center sm:justify-content-end">
          <div className="text-28px font-bold mx-auto sm:ml-4 drilldownHeader">
            New Entry
          </div>
        </div>
        <div className="mt-5 ml-4">
          <FormProvider {...formMethods}>
            {!createClaim.isSuccess && (
              <form className="w-full mx-auto flex flex-column gap-5">
                <ClaimsForm showUpdateFields={false} />
                <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
                  <ConfirmationDialog
                    Button={
                      <LoadingButton
                        isLoading={createClaim.isPending}
                        label="Create"
                        className="w-8rem bluwaiBlue border-0 flex m-auto py-3"
                        fontSize="text-20px"
                        type="button"
                      />
                    }
                    onConfirm={handleSubmit(onSubmit)}
                    message="Are you sure you want to create a new Claim?"
                  />
                </div>
              </form>
            )}
            {createClaim.isSuccess && (
              <div className="w-full mx-auto flex flex-column gap-5">
                <div className="w-fit mx-auto flex flex-column">
                  <div className="text-center text-40px font-bold text-green-500 mb-3">
                    Success!
                  </div>
                  <i className="pi pi-check-circle mx-auto text-40px text-green-500 mb-3" />
                  <div className="text-standard mt-3 mx-auto">
                    The Claim has been created successfully!
                  </div>
                </div>
              </div>
            )}
          </FormProvider>
          <ErrorToast toastRef={toast} />
        </div>
      </div>
    </Sidebar>
  );
};

export default NewClaimDrilldown;
